
import React, { Component } from 'react'
import Transition from 'react-transition-group/Transition'

type EnterHandler = (node: HTMLElement, isAppearing: boolean) => void
type ExitHandler = (node: HTMLElement) => void

type TransitionClasses = {
  base?: string,
  beforeEnter?: string,
  entering?: string,
  entered?: string,
  exiting?: string,
  exited?: string
}

type Props = {
  children: React.ReactNode,
  className?: string,
  appear?: boolean,
  in?: boolean,
  mountOnEnter?: boolean,
  unmountOnExit?: boolean,
  onEnter?: EnterHandler,
  onEntered?: EnterHandler,
  onExit?: ExitHandler,
  onExited?: ExitHandler,
  transitionClasses: TransitionClasses
}

type State = {
  animationStage: string
}

export default class CSSTransition extends Component<Props, State> {
  transitionDoneHandlerToCleanUp: (() => void)
  elementToCleanUp: HTMLElement

  constructor (props: Props) {
    super(props)

    this.state = {
      animationStage: (props.appear || !props.in) ? 'beforeEnter' : 'entered'
    }

    this.enter = this.enter.bind(this) as this['enter']
    this.afterEnter = this.afterEnter.bind(this) as this['afterEnter']
    this.exit = this.exit.bind(this) as this['exit']
    this.afterExit = this.afterExit.bind(this) as this['afterExit']
    this.addEndListener = this.addEndListener.bind(this) as this['addEndListener']
  }

  shouldComponentUpdate (nextProps: Props, nextState: State) {
    return (
      nextProps.in !== this.props.in ||
      nextState.animationStage !== this.state.animationStage ||
      nextProps.children !== this.props.children
    )
  }

  enter = (node: HTMLElement | boolean, isAppearing?: boolean) => {
    if (typeof node !== "boolean") {
      //  Skip entering if we can't possibly trigger the transition
      if (!this.props.transitionClasses.entering && !this.props.transitionClasses.beforeEnter) {
        this.afterEnter(node, isAppearing)
        return
      }

      //  If element is transitioning when it enters, it won't get back into beforeEnter position
      //  instantly to start an enter transition. We will snap it back into place by forcing no
      //  transition style, then reallowing it right before starting the enter transition.
      let oldTransitionStyle: string
      if (this.state.animationStage !== 'beforeEnter') {
        oldTransitionStyle = node.style.transition || ''
        node.style.transition = 'none'
        this.setState({
          animationStage: 'beforeEnter'
        })
      }

      requestAnimationFrame(() => {
        void node.offsetLeft //  reflow here fixes ios bug
        if (typeof oldTransitionStyle !== 'undefined') node.style.transition = oldTransitionStyle
        this.setState({
          animationStage: 'entering'
        })
      })

      if (this.props.onEnter) this.props.onEnter(node, isAppearing)
    }
  }

  afterEnter = (node: HTMLElement | boolean, isAppearing?: boolean) => {
    if (typeof node !== "boolean") {
      this.setState({
        animationStage: 'entered'
      })

      if (this.props.onEntered) this.props.onEntered(node, isAppearing)
    }
  }

  exit = (node?: HTMLElement) => {
    //  Skip exiting if we can't possibly trigger the transition
    if (!this.props.transitionClasses.exiting) {
      this.afterExit(node)
      return
    }

    this.setState({
      animationStage: 'exiting'
    })

    if (this.props.onExit) this.props.onExit(node)
  }

  afterExit = (node?: HTMLElement) => {
    this.setState({
      animationStage: 'exited'
    })

    if (this.props.onExited) this.props.onExited(node)
  }

  componentWillUnmount () {
    if (!this.elementToCleanUp) return

    this.elementToCleanUp.removeEventListener('transitionend', this.transitionDoneHandlerToCleanUp)
    this.elementToCleanUp.removeEventListener('animationend', this.transitionDoneHandlerToCleanUp)
  }

  addEndListener = (element: (HTMLElement | (() => void)), done?: () => void) => {
    if (typeof element !== "function") {
      element.removeEventListener('transitionend', this.transitionDoneHandlerToCleanUp)
      element.removeEventListener('animationend', this.transitionDoneHandlerToCleanUp)

      element.addEventListener('transitionend', done)
      element.addEventListener('animationend', done)

      this.elementToCleanUp = element
      this.transitionDoneHandlerToCleanUp = done
    }
  }

  render () {
    const {
      children,
      transitionClasses,
      className,
      mountOnEnter,
      in: inProp, // can't name a variable 'in' it's reserved
      unmountOnExit
    } = this.props
    const child = children as React.ReactElement<{ className: string }>
    React.Children.only(child)

    const newClassName = [
      className,
      child['props'] && child['props'].className,
      transitionClasses['base'],
      transitionClasses[this.state.animationStage]
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <Transition
        in={inProp}
        className={newClassName}
        addEndListener={this.addEndListener}
        onEnter={this.enter}
        onEntered={this.afterEnter}
        onExit={this.exit}
        onExited={this.afterExit}
        mountOnEnter={mountOnEnter}
        unmountOnExit={unmountOnExit}
      >
        {child}
      </Transition>
    )
  }
}
